import { useEffect, useState } from 'react';
import { 
  initSmoothScroll, 
  initRevealAnimations, 
  initParallaxEffects,
  initHorizontalScroll
} from '../utils/smoothScroll';

/**
 * Custom hook to initialize smooth scrolling and scroll-based animations
 * @param {Object} options - Configuration options
 * @param {boolean} options.enableReveal - Enable reveal animations
 * @param {boolean} options.enableParallax - Enable parallax effects
 * @param {boolean} options.enableHorizontalScroll - Enable horizontal scroll sections
 * @param {string} options.horizontalScrollSelector - Selector for horizontal scroll containers
 * @returns {Object} The Lenis instance and loading state
 */
const useSmoothScroll = ({
  enableReveal = true,
  enableParallax = true,
  enableHorizontalScroll = false,
  horizontalScrollSelector = '.horizontal-scroll'
} = {}) => {
  const [lenis, setLenis] = useState(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Initialize smooth scrolling
    const { lenis: lenisInstance, cleanup } = initSmoothScroll();
    setLenis(lenisInstance);

    // Wait for DOM to be fully loaded
    const timer = setTimeout(() => {
      // Initialize scroll-based animations
      if (enableReveal) initRevealAnimations();
      if (enableParallax) initParallaxEffects();
      if (enableHorizontalScroll) initHorizontalScroll(horizontalScrollSelector);
      
      setIsReady(true);
    }, 100);

    // Cleanup function
    return () => {
      clearTimeout(timer);
      cleanup();
    };
  }, [enableReveal, enableParallax, enableHorizontalScroll, horizontalScrollSelector]);

  return { lenis, isReady };
};

export default useSmoothScroll;
